import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Close, DirectionsCar, PersonAdd, LocalShipping, CheckCircle, HourglassTop } from "@mui/icons-material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Tooltip from "@mui/material/Tooltip";

const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: '"Cairo", "Tajawal", Arial, sans-serif',
  },
});

const API_BASE = "https://dash.tatabu.io/back-end/public/api/taxi-orders";

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10 && cleaned.startsWith("05")) {
    return "966" + cleaned.slice(1);
  } else if (cleaned.length === 9 && cleaned.startsWith("5")) {
    return "966" + cleaned;
  } else if (cleaned.length === 12 && cleaned.startsWith("966")) {
    return cleaned;
  }
  return cleaned.startsWith("0") ? "966" + cleaned.slice(1) : cleaned;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const formatTime12h = (timeStr) => {
  if (!timeStr) return null;
  const [hourStr, minuteStr] = timeStr.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  const period = hour >= 12 ? "م" : "ص";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${period}`;
};

const statusMap = {
  pending: { label: "بانتظار اضافة سائق", color: "warning" },
  confirmed: { label: "بانتظار التوصيل", color: "success" },
  shipped: { label: "تم التوصيل", color: "info" },
  // delivered: { label: "مُسلَّم", color: "primary" },
  cancelled: { label: "ملغي", color: "error" },
};

// Step definitions for the status stepper
const statusSteps = [
  {
    key: "pending",
    label: "تم اضافة سائق",
    icon: PersonAdd,
    color: "#ed6c02",
    bgColor: "#fff3e0",
    borderColor: "#ffcc80",
  },
  {
    key: "confirmed",
    label: "بانتظار التوصيل",
    icon: HourglassTop,
    color: "#2e7d32",
    bgColor: "#e8f5e9",
    borderColor: "#a5d6a7",
  },
  {
    key: "shipped",
    label: "تم التوصيل",
    icon: CheckCircle,
    color: "#0277bd",
    bgColor: "#e3f2fd",
    borderColor: "#90caf9",
  },
];

const getStepIndex = (status) => {
  const idx = statusSteps.findIndex((s) => s.key === status);
  return idx === -1 ? 0 : idx;
};

// Status Stepper Component
const StatusStepper = ({ currentStatus }) => {
  const currentIndex = getStepIndex(currentStatus);

  return (
    <Box sx={{ my: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0}>
        {statusSteps.map((step, i) => {
          const isCompleted = i < currentIndex;
          const isActive = i === currentIndex;
          const IconComponent = step.icon;

          return (
            <Stack key={step.key} direction="row" alignItems="center" sx={{ flex: i < statusSteps.length - 1 ? 1 : "unset" }}>
              {/* Step circle */}
              <Stack alignItems="center" gap={0.5} sx={{ minWidth: 72 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isActive ? step.bgColor : isCompleted ? "#e8f5e9" : "#f5f5f5",
                    border: `2px solid ${isActive ? step.color : isCompleted ? "#66bb6a" : "#e0e0e0"}`,
                    transition: "all 0.3s ease",
                    boxShadow: isActive ? `0 0 0 4px ${step.bgColor}` : "none",
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 22,
                      color: isActive ? step.color : isCompleted ? "#66bb6a" : "#bdbdbd",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Cairo", sans-serif',
                    fontSize: "0.65rem",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? step.color : isCompleted ? "#66bb6a" : "#aaa",
                    textAlign: "center",
                    lineHeight: 1.3,
                    maxWidth: 72,
                  }}
                >
                  {step.label}
                </Typography>
              </Stack>

              {/* Connector line */}
              {i < statusSteps.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 3,
                    borderRadius: 2,
                    bgcolor: isCompleted || (isActive && i < currentIndex) ? "#66bb6a" : "#e0e0e0",
                    mb: 2.8,
                    mx: 0.5,
                    transition: "background-color 0.3s ease",
                  }}
                />
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default function TaxiOperation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "tomorrow" ? "tomorrow" : "today");
  const [todayOrders, setTodayOrders] = useState([]);
  const [tomorrowOrders, setTomorrowOrders] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [tomorrowDate, setTomorrowDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverAmount, setDriverAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // change status modal
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmRow, setStatusConfirmRow] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/today-and-tomorrow`);
        const json = await res.json();
        if (json.success) {
          const mapOrders = (orders) =>
            orders.map((item, index) => ({
              id: item.id,
              index: index + 1,
              order_number: item.order_number,
              type: item.type,
              extra_route_access: item.extra_route_access,
              car_model: item.car_model,
              seats_number: item.seats_number,
              price: item.price,
              name: item.name,
              phone: item.phone,
              pickup_location: item.pickup_location,
              neighborhood: item.neighborhood,
              pickup_date: item.pickup_date,
              pickup_hour: item.pickup_hour,
              hotel_name: item.hotel_name,
              status: item.status,
              driver_name: item.driver_name,
              driver_phone_number: item.driver_phone_number,
              driver_amount: item.driver_amount,
              images: item.images || [],
            }));
          setTodayOrders(mapOrders(json.data.today.orders));
          setTomorrowOrders(mapOrders(json.data.tomorrow.orders));
          setTodayDate(json.data.today.date);
          setTomorrowDate(json.data.tomorrow.date);
        }
      } catch (e) {
        setError("فشل في تحميل البيانات");
      }
      setLoading(false);
    };
    fetchOrders();
  }, []);

  const handleOpenTicket = (row) => {
    if (!row.images || row.images.length === 0) {
      alert("لا توجد تذكرة طيران");
      return;
    }

    window.open(row.images[0].full_path, "_blank");
  };

  //
  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setDriverName(row.driver_name || "");
    setDriverPhone(row.driver_phone_number || "");
    setDriverAmount(row.driver_amount != null ? String(row.driver_amount) : "");
    setSaveError(null);
    setSaveSuccess(false);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedRow) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await fetch(`${API_BASE}/update-driver`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedRow.id,
          driver_name: driverName,
          driver_phone_number: driverPhone,
          driver_amount: driverAmount !== "" ? Number(driverAmount) : null,
        }),
      });
      const json = await res.json();
      if (json.success || res.ok) {
        setSaveSuccess(true);
        const updateOrders = (orders) =>
          orders.map((o) => {
            if (o.id !== selectedRow.id) return o;
            const updatedDriverName = driverName;
            const updatedDriverPhone = driverPhone;
            const updatedDriverAmount = driverAmount !== "" ? Number(driverAmount) : null;
            const allFilled = !!updatedDriverName && !!updatedDriverPhone && updatedDriverAmount != null;
            return {
              ...o,
              driver_name: updatedDriverName,
              driver_phone_number: updatedDriverPhone,
              driver_amount: updatedDriverAmount,
              status: allFilled ? "confirmed" : "pending",
            };
          });
        setTodayOrders((prev) => updateOrders(prev));
        setTomorrowOrders((prev) => updateOrders(prev));
        setTimeout(() => handleCloseModal(), 1200);
      } else {
        setSaveError(json.message || "حدث خطأ أثناء الحفظ");
      }
    } catch (e) {
      setSaveError("فشل الاتصال بالخادم");
    }
    setSaving(false);
  };

  const activeRows = activeTab === "today" ? todayOrders : tomorrowOrders;

  const CountBadge = ({ count, isActive }) => (
    <Box
      component="span"
      sx={{
        mr: 1,
        bgcolor: isActive ? "rgba(255,255,255,0.25)" : "primary.main",
        color: "#fff",
        borderRadius: "50px",
        px: 1,
        py: 0.15,
        fontSize: "0.75rem",
        fontWeight: 800,
        minWidth: 22,
        display: "inline-block",
        textAlign: "center",
        lineHeight: 1.6,
        border: isActive ? "1px solid rgba(255,255,255,0.4)" : "none",
      }}
    >
      {count}
    </Box>
  );

  const handleStatusChipClick = (row) => {
    setStatusConfirmRow(row);
    setStatusConfirmOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirmRow) return;
    setStatusUpdating(true);
    try {
      const res = await fetch(`${API_BASE}/mark-as-shipped/${statusConfirmRow.id}`, {
        method: "POST",
      });
      const json = await res.json();
      if (json.success || res.ok) {
        const updateOrders = (orders) => orders.map((o) => (o.id === statusConfirmRow.id ? { ...o, status: "shipped" } : o));
        setTodayOrders((prev) => updateOrders(prev));
        setTomorrowOrders((prev) => updateOrders(prev));
      }
    } catch (e) {
      console.error("Failed to update status", e);
    }
    setStatusUpdating(false);
    setStatusConfirmOpen(false);
    setStatusConfirmRow(null);
  };

  const handleStatusConfirmClose = () => {
    setStatusConfirmOpen(false);
    setStatusConfirmRow(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />

      <Box dir="rtl">
        {loading && (
          <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Tab Buttons */}
        <Stack direction="row" gap={2} mt={2} mb={3} sx={{ justifyContent: "center", paddingLeft: "8px", paddingRight: "8px" }}>
          <Button
            variant={activeTab === "today" ? "contained" : "outlined"}
            color="primary"
            onClick={() => setActiveTab("today")}
            sx={{
              flex: { xs: 1, sm: "unset" },
              fontWeight: 700,
              borderRadius: 2,
              px: { xs: 2, sm: 4 },
              py: 1.2,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              boxShadow: activeTab === "today" ? 3 : 0,
              gap: 0.5,
            }}
          >
            طلبات اليوم
            <CountBadge count={todayOrders.length} isActive={activeTab === "today"} />
          </Button>

          <Button
            variant={activeTab === "tomorrow" ? "contained" : "outlined"}
            color="primary"
            onClick={() => setActiveTab("tomorrow")}
            sx={{
              flex: { xs: 1, sm: "unset" },
              fontWeight: 700,
              borderRadius: 2,
              px: { xs: 2, sm: 4 },
              py: 1.2,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              boxShadow: activeTab === "tomorrow" ? 3 : 0,
              gap: 0.5,
            }}
          >
            طلبات الغد
            <CountBadge count={tomorrowOrders.length} isActive={activeTab === "tomorrow"} />
          </Button>
        </Stack>

        {error && (
          <Box sx={{ px: 2, mb: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
          {!loading && activeRows.length === 0 && (
            <Typography align="center" sx={{ mt: 6, color: "#999", fontFamily: '"Cairo", sans-serif', fontSize: "1rem" }}>
              لا توجد طلبات
            </Typography>
          )}

          <Grid container spacing={2}>
            {activeRows.map((row) => {
              const statusInfo = statusMap[row.status] || { label: row.status, color: "default" };
              const hasDriverData = !!(row.driver_name && row.driver_phone_number && row.driver_amount != null);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily: '"Cairo", sans-serif',
                      // border: hasDriverData ? "1px solid #1976d2" : "1px solid #bf360c",
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: 6 },
                    }}
                  >
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography
                          sx={{
                            fontFamily: '"Cairo", sans-serif',
                            fontWeight: 800,
                            fontSize: "0.8rem",
                            color: "#888",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.3,
                            borderRadius: 1,
                          }}
                        >
                          {row.order_number}
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1}>
                          {row.images && row.images.length > 0 && (
                            <Tooltip title="تذكرة الطيران">
                              <PhotoLibraryIcon
                                onClick={() => handleOpenTicket(row)}
                                sx={{
                                  fontSize: 20,
                                  cursor: "pointer",
                                  color: "#222",
                                  "&:hover": { color: "#0d47a1" },
                                }}
                              />
                            </Tooltip>
                          )}

                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.color}
                            size="small"
                            onClick={row.status === "confirmed" ? () => handleStatusChipClick(row) : undefined}
                            sx={{
                              fontFamily: '"Cairo", sans-serif',
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              cursor: row.status === "confirmed" ? "pointer" : "default",
                            }}
                          />
                        </Stack>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={0.8} mb={1}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الخدمة:</Typography>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, fontSize: "0.88rem", color: "#222" }}>{row.type || "—"}</Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={0.8} mb={1}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>السيارة:</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <DirectionsCar sx={{ fontSize: 16, color: "#1565c0" }} />
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>{row.car_model || "—"}</Typography>
                        </Stack>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.5}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>مبلغ البيع:</Typography>
                        <Box
                          sx={{
                            // bgcolor: "#d33030",
                            color: "#333",
                            // px: 1.2,
                            // py: 0.2,
                            borderRadius: 1,
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            fontFamily: '"Cairo", sans-serif',
                          }}
                        >
                          {row.price} ﷼
                        </Box>
                      </Stack>
                    </CardContent>

                    {/* <Divider /> */}

                    <CardActions sx={{ px: 2, py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenModal(row)}
                        sx={{
                          fontFamily: '"Cairo", sans-serif',
                          fontWeight: 700,
                          borderRadius: 2,
                          fontSize: "0.82rem",
                          py: 0.8,
                          bgcolor: hasDriverData ? "#1976d2" : "#e65100",
                          "&:hover": { bgcolor: hasDriverData ? "#1976d2" : "#bf360c" },
                        }}
                      >
                        {hasDriverData ? "تعديل البيانات" : "اكمال البيانات"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>

      {/* Driver Details Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="xs" fullWidth dir="rtl" PaperProps={{ sx: { borderRadius: 3, fontFamily: '"Cairo", sans-serif' } }}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#1565c0",
            color: "#fff",
            fontFamily: '"Cairo", sans-serif',
            fontWeight: 700,
            fontSize: "1rem",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <DirectionsCar sx={{ fontSize: 20 }} />
            <span>تفاصيل السائق</span>
          </Stack>
          <IconButton onClick={handleCloseModal} size="small" sx={{ color: "#fff" }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <Stack sx={{ marginBottom: "20px" }} gap={2.5}>
            <TextField
              label="اسم السائق"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
            />
            <TextField
              label="رقم السائق"
              value={driverPhone}
              onChange={(e) => setDriverPhone(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
              placeholder="05xxxxxxxx"
              dir="ltr"
              type="tel"
            />
            <TextField
              label="مبلغ السائق (﷼)"
              value={driverAmount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || /^\d*\.?\d*$/.test(val)) setDriverAmount(val);
              }}
              fullWidth
              size="small"
              inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo", sans-serif' } }}
              InputLabelProps={{ style: { fontFamily: '"Cairo", sans-serif' } }}
              type="number"
            />
          </Stack>

          {selectedRow && (
            <>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  color: "#555",
                  fontFamily: '"Cairo", sans-serif',
                  bgcolor: "#f5f5f5",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.8,
                  fontSize: "0.82rem",
                }}
              >
                رقم الطلب: <strong>{selectedRow.order_number}</strong> — {selectedRow.type}
                {selectedRow.car_model ? ` — ${selectedRow.car_model}` : ""}
              </Typography>

              <Box sx={{ mb: 1, bgcolor: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
                {[
                  { label: "اسم العميل", value: selectedRow.name },
                  { label: "رقم الجوال", value: selectedRow.phone, ltr: true, whatsapp: true },
                  {
                    label: "تاريخ الاستقبال",
                    value:
                      selectedRow.pickup_date || selectedRow.pickup_hour
                        ? `${formatDate(selectedRow.pickup_date) || ""} ${selectedRow.pickup_hour ? "— " + formatTime12h(selectedRow.pickup_hour) : ""}`.trim()
                        : null,
                    ltr: false,
                  },
                  { label: "موقع الاستقبال", value: selectedRow.pickup_location },
                  { label: "الحي", value: selectedRow.neighborhood === "N/A" ? null : selectedRow.neighborhood },
                  { label: "اسم الفندق", value: selectedRow.hotel_name },
                  { label: "مبلغ البيع", value: selectedRow.price ? `${selectedRow.price} ﷼` : null, highlight: true },
                ]
                  .filter((r) => r.value != null && r.value !== "")
                  .map((row, i, arr) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1.5,
                        py: 0.9,
                        borderBottom: i < arr.length - 1 ? "1px solid #eee" : "none",
                        bgcolor: i % 2 === 0 ? "#fff" : "#f9f9f9",
                      }}
                    >
                      <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.78rem", color: "#888", minWidth: 90 }}>{row.label}</Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Cairo", sans-serif',
                          fontSize: "0.82rem",
                          fontWeight: row.highlight ? 800 : 600,
                          color: row.highlight ? "#d33030" : row.whatsapp ? "#25D366" : "#222",
                          direction: row.ltr ? "ltr" : "rtl",
                          textAlign: "left",
                        }}
                      >
                        {row.whatsapp ? (
                          <a
                            href={`https://wa.me/${formatPhoneForWhatsApp(row.value)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#25D366", textDecoration: "none", fontWeight: 700 }}
                          >
                            📱 {row.value}
                          </a>
                        ) : (
                          row.value
                        )}
                      </Typography>
                    </Stack>
                  ))}
              </Box>
            </>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mt: 2, fontFamily: '"Cairo", sans-serif', fontSize: "0.82rem", "& .MuiAlert-icon": { marginRight: 0, marginLeft: "12px" } }}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2, fontFamily: '"Cairo", sans-serif', fontSize: "0.82rem", "& .MuiAlert-icon": { marginRight: 0, marginLeft: "12px" } }}>
              تم الحفظ بنجاح
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            color="inherit"
            disabled={saving}
            sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, borderRadius: 2, flex: 1 }}
          >
            إلغاء
          </Button>
          <Button
            dir="rtl"
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, borderRadius: 2, flex: 2 }}
          >
            {saving ? "جاري الحفظ..." : "حفظ"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chip Status Confirm Modal */}
      <Dialog
        open={statusConfirmOpen}
        onClose={handleStatusConfirmClose}
        maxWidth="xs"
        fullWidth
        dir="rtl"
        PaperProps={{ sx: { borderRadius: 3, fontFamily: '"Cairo", sans-serif' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1565c0",
            color: "#fff",
            fontFamily: '"Cairo", sans-serif',
            fontWeight: 700,
            fontSize: "1rem",
            py: 1.5,
            px: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>تأكيد تغيير الحالة</span>
          <IconButton onClick={handleStatusConfirmClose} size="small" sx={{ color: "#fff" }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1, px: 3 }}>
          {/* 3-Step Status Stepper */}
          <StatusStepper currentStatus={statusConfirmRow ? "confirmed" : "pending"} />

          <Divider sx={{ mb: 2 }} />

          {/* Confirmation text */}
          <Box
            sx={{
              bgcolor: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: 2,
              px: 2,
              py: 1.5,
              mb: 1.5,
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
              <CheckCircle sx={{ color: "#0277bd", fontSize: 20 }} />
              <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.95rem", fontWeight: 700, color: "#0277bd" }}>تأكيد التوصيل</Typography>
            </Stack>
            <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.85rem", color: "#555", pr: 3.5 }}>
              هل تريد تغيير حالة الطلب إلى <strong style={{ color: "#0277bd" }}>تم التوصيل</strong>؟
            </Typography>
          </Box>

          {statusConfirmRow && (
            <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.8rem", color: "#888", mt: 1 }}>
              الطلب رقم: #{statusConfirmRow.index} — {statusConfirmRow.type}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleStatusConfirmClose}
            variant="outlined"
            color="inherit"
            disabled={statusUpdating}
            sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, borderRadius: 2, flex: 1 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            color="info"
            disabled={statusUpdating}
            sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, borderRadius: 2, flex: 2 }}
          >
            {statusUpdating ? "جاري التحديث..." : "تأكيد التوصيل"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
